import React, { useCallback, useMemo } from 'react';
import { PriceInput } from '@common-fe/common-fe';

import { REQUIRED_TEXT } from '@/common/constants';
import { IInvoiceStatusType, IInvoiceType } from '@/types';

import { useApproveEoyReconReturnQuery } from '../queries/useApproveEoyReconReturn.query';
import { useApprovePrefundReturnQuery } from '../queries/useApprovePrefundReturn.query';

import { useIsInvoiceNeedApprove } from './useIsInvoiceNeedApprove';

interface Props {
  invoiceType: IInvoiceType;
  invoiceStatus: IInvoiceStatusType;
  initialAmount?: number;
  invoiceId?: string;
  isPrefundExist: boolean;
  refetchInvoice: () => void;
  refetchPrefund: () => void;
}

export const useApproveInvoice = ({
  invoiceType,
  invoiceStatus,
  initialAmount,
  invoiceId,
  isPrefundExist,
  refetchInvoice,
  refetchPrefund,
}: Props) => {
  const {
    isInvoiceNeedApprove,
    amount,
    setAmount,
    refetchStatus,
    isError,
    setIsSubmitted,
    isSuccess,
    setIsSuccess,
    eoyReconPlanName,
  } = useIsInvoiceNeedApprove({
    type: invoiceType,
    status: invoiceStatus,
    initialAmount,
    invoiceId,
  });

  const { handleSave: handleApprovePrefund, isLoading: isPrefundApproveLoading } = useApprovePrefundReturnQuery();
  const { handleSave: handleApproveEoyRecon, isLoading: isEoyReconApproveLoading } = useApproveEoyReconReturnQuery();

  const currentHandleApprove = useMemo(() => {
    if (invoiceType === IInvoiceType.EOY_PLAN_RECONCILIATION) return handleApproveEoyRecon;
    return handleApprovePrefund;
  }, [invoiceType, handleApprovePrefund, handleApproveEoyRecon]);

  const handleApprove = useCallback(async () => {
    setIsSubmitted(true);
    if (isError) return;
    if (invoiceId && amount) {
      await currentHandleApprove({
        invoice_id: invoiceId,
        invoice_amount: parseFloat(amount),
      });
      setIsSuccess(true);
    }
  }, [setIsSubmitted, isError, invoiceId, amount, currentHandleApprove, setIsSuccess]);

  const handleRefetchStatus = useCallback(() => {
    refetchInvoice();
    if (refetchStatus) refetchStatus();
    if (isPrefundExist) {
      refetchPrefund();
    }
    setIsSuccess(false);
  }, [refetchInvoice, refetchStatus, isPrefundExist, refetchPrefund, setIsSuccess]);

  const amountInput = useMemo(() => {
    if (isInvoiceNeedApprove) {
      return React.createElement(PriceInput, {
        value: amount,
        placeholder: 'Enter $ amount',
        name: 'amount',
        onChange: (value: string) => setAmount(value),
        testId: 'FinOpsPrefund_amount_input',
        errorText: isError ? REQUIRED_TEXT : undefined,
        allowNegativeValue: true,
      });
    }
    return undefined;
  }, [amount, isInvoiceNeedApprove, setAmount, isError]);

  return {
    isInvoiceNeedApprove,
    isApproveLoading: isPrefundApproveLoading || isEoyReconApproveLoading,
    isSuccess,
    handleApprove,
    handleRefetchStatus,
    amountInput,
    amount,
    eoyReconPlanName,
  };
};
